/**
 * i18n non-component exports.
 *
 * Split from `provider.tsx` so React Fast Refresh can re-render the
 * `I18nProvider` component without invalidating the module that
 * holds the pure utilities.
 */

import { createContext } from 'react';
import type { Locale, LocaleMessages, StringParams } from './types';
import { en } from './locales/en';
import { reportMetric } from '../observability/telemetry';

const REGISTRY: Record<Locale, LocaleMessages> = {
  en,
};

export function listLocales(): LocaleMessages[] {
  return Object.values(REGISTRY);
}

export function getLocaleMessages(locale: Locale): LocaleMessages {
  return REGISTRY[locale] ?? REGISTRY.en;
}

/**
 * Does this locale (or `en`) define `key`?
 *
 * `resolveMessage` answers a missing key with `⟦key⟧` and a metric,
 * which is right for a string that SHOULD exist. Help mode has keys
 * that legitimately may not -- a per-role line is written only for the
 * roles whose answer differs -- and probing those by string-matching
 * the ⟦⟧ marker would both lie (a real message could contain it) and
 * fire a missing-key metric on every render of a working screen.
 */
export function hasMessage(locale: Locale, key: string): boolean {
  return (
    getLocaleMessages(locale).messages[key] !== undefined ||
    REGISTRY.en.messages[key] !== undefined
  );
}

export function resolveMessage(
  locale: Locale,
  key: string,
  params?: StringParams,
): string {
  const msgs = getLocaleMessages(locale);
  let value = msgs.messages[key];
  if (value === undefined && locale !== 'en') {
    value = REGISTRY.en.messages[key];
    reportMetric('i18n.fallback', 1, { locale, key });
  }
  if (value === undefined) {
    reportMetric('i18n.missing_key', 1, { locale, key });
    return `⟦${key}⟧`;
  }
  return interpolate(value, params);
}

function interpolate(template: string, params?: StringParams): string {
  if (!params) return template;
  return template.replace(/%\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined ? `%{${name}}` : String(v);
  });
}

export const I18nContext = createContext<unknown>(null);
